// Pending project/context names + duplicate-zone highlight for InboxView.
import { useCallback, useEffect, useState } from 'react';
import {
  normalizeNewContextInput,
  normalizeNewProjectInput,
} from '@/features/organize/utils/inboxTriageUtils';

/** How long a duplicate-zone flash stays highlighted after add. */
export const HIGHLIGHT_ZONE_CLEAR_MS = 600;

export function useInboxPendingTags({
  contextDisplayMap,
  projectDisplayMap,
}) {
  const [pendingContextNames, setPendingContextNames] = useState(
    () => new Map(),
  );
  const [pendingProjectNames, setPendingProjectNames] = useState(
    () => new Map(),
  );
  const [highlightZoneId, setHighlightZoneId] = useState(null);

  useEffect(() => {
    setPendingContextNames((prev) => {
      const next = new Map(prev);
      for (const id of [...next.keys()]) {
        if (contextDisplayMap.has(id)) next.delete(id);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [contextDisplayMap]);

  useEffect(() => {
    setPendingProjectNames((prev) => {
      const next = new Map(prev);
      for (const id of [...next.keys()]) {
        if (projectDisplayMap.has(id)) next.delete(id);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [projectDisplayMap]);

  const handleAddContext = useCallback(
    (raw) => {
      const parsed = normalizeNewContextInput(raw);
      if (!parsed) return 'invalid';
      const { id, name } = parsed;
      const zoneId = `triage-context-${id}`;
      if (contextDisplayMap.has(id) || pendingContextNames.has(id)) {
        setHighlightZoneId(zoneId);
        window.setTimeout(
          () => setHighlightZoneId(null),
          HIGHLIGHT_ZONE_CLEAR_MS,
        );
        return 'duplicate';
      }
      setPendingContextNames((prev) => new Map(prev).set(id, name));
      return 'added';
    },
    [contextDisplayMap, pendingContextNames],
  );

  const handleAddProject = useCallback(
    (raw) => {
      const parsed = normalizeNewProjectInput(raw);
      if (!parsed) return 'invalid';
      const { id, name } = parsed;
      const zoneId = `triage-project-${id}`;
      if (projectDisplayMap.has(id) || pendingProjectNames.has(id)) {
        setHighlightZoneId(zoneId);
        window.setTimeout(
          () => setHighlightZoneId(null),
          HIGHLIGHT_ZONE_CLEAR_MS,
        );
        return 'duplicate';
      }
      setPendingProjectNames((prev) => new Map(prev).set(id, name));
      return 'added';
    },
    [projectDisplayMap, pendingProjectNames],
  );

  return {
    pendingContextNames,
    pendingProjectNames,
    highlightZoneId,
    handleAddContext,
    handleAddProject,
  };
}
